// src/pages/profile.jsx
import React, { useEffect, useState } from 'react';
import {
  User, UserPlus, Users, Mail, DollarSign, Settings, LogOut,
  CreditCard, Menu, ChevronRight, User2, RotateCcw, FileText, MessageCircle,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';

import ContactMembers from '../components/profile/ContactMembers';
import Content from '../components/profile/Content';
import NewMember from '../components/profile/NewMember';
import SettingsProfile from '../components/profile/Settings';
import LogoProfile from '../components/profile/LogoProfile';

import { useAuth } from '../context/authContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';

import { ActionLoader, ErrorPage, PageLoader } from '../components/loader';
import MembersPanel from '../components/profile/Members/MembersPanel';
import Swal from 'sweetalert2';
import FusionCompanies from '../components/profile/FusionCompanies';
import MyQuotasAdmin from '../components/profile/Quotas/MyQuotasAdmin';
import MyQuotasPartner from '../components/profile/Quotas/MyQuotasPartner';
import ChangeRut from '../components/profile/ChangeRut';

// Vistas “ocultas”
import AnnualReportsCard from '../components/profile/Documents/AnnualReportsCard';
import StatutesCard from '../components/profile/Documents/StatutesReportsCard';
import EthicsReportsCard from '../components/profile/Documents/EthicsReportsCard';
import CrimePreventionCard from '../components/profile/Documents/PreventionReportsCard';
import ProtocolsCard from '../components/profile/Documents/ProtocolsCard'; 
import InternalRegulationCard from '../components/profile/Documents/InternalRegulationCard';
import MaterialsReportsCard from '../components/profile/Documents/MaterialReportsCards';
import PopupsCard from '../components/profile/Documents/PopupsCard';

// Panel de Documentación Oficial
import OfficialDocsAdmin from '../components/profile/OfficialDocsAdmin';
import AdminChatbot from './admin/AdminChatbot';

const icons = {
  profile: <User className="mr-3" size={18} />,
  newMember: <UserPlus className="mr-3" size={18} />,
  members: <Users className="mr-3" size={18} />,
  contact: <Mail className="mr-3" size={18} />,
  manageQuotas: <DollarSign className="mr-3" size={18} />,
  quotas: <CreditCard className="mr-3" size={18} />,
  settings: <Settings className="mr-3" size={18} />,
  fusion: <User2 className="mr-3" size={18} />,
  changeRut: <RotateCcw className="mr-3" size={18} />,
  chatbot: <MessageCircle className="mr-3" size={18} />,
  officialDocs: <FileText className="mr-3" size={18} />,
};

const getMenuItems = (role) =>
  role === 'admin'
    ? [
        { id: 'profile', label: 'Mi Perfil', icon: icons.profile },
        { id: 'new-member', label: 'Nuevo Socio', icon: icons.newMember },
        { id: 'members', label: 'Lista de Socios', icon: icons.members },
        { id: 'contacts', label: 'Contacto de socios', icon: icons.contact },
        { id: 'my-contacts', label: 'Mis Contactos', icon: icons.members },
        { id: 'manage-quotas', label: 'Administrar Cuotas', icon: icons.manageQuotas },
        { id: 'official-docs', label: 'Documentación', icon: icons.officialDocs },
        { id: 'chatbot', label: 'Asistente RDS', icon: icons.chatbot },
        { id: 'fusion', label: 'Fusión de Compañías', icon: icons.fusion },
        { id: 'change-rut', label: 'Cambiar RUT', icon: icons.changeRut },
      ]
    : [
        { id: 'profile', label: 'Mi Perfil', icon: icons.profile },
        { id: 'contacts', label: 'Mis Contactos', icon: icons.members },
        { id: 'manage-quotas', label: 'Administrar Cuotas', icon: icons.manageQuotas },
      ];

/* ---------- helpers locales (no tocan services) ---------- */
const jsonOrThrow = async (res) => {
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) {
    const msg = (data && (data.message || data.error || data.detail)) || `HTTP error! status: ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const isNotFoundLike = (err) =>
  err && (err.status === 404 || (err.status === 400 && /no encontrada/i.test(String(err.message))));

const normalizeRut = (rut) => String(rut || '').trim().toUpperCase().replace(/\./g, '');
const rutVariants = (rut) => {
  const base = normalizeRut(rut);
  const noDash = base.replace(/-/g, '');
  return Array.from(new Set([base, noDash])).filter(Boolean);
};

/** Renderiza el contenido según pestaña */
const getContent = (
  tab,
  role,
  detailsCompany,
  onUpdate,
  setIsSubmitting,
  goToTab
) => {
  const defaultContent = (
    <Content detailsCompany={detailsCompany} onUpdate={onUpdate} role={role} isSubmitting={setIsSubmitting} />
  );

  const views = {
    profile: defaultContent,
    settings: <SettingsProfile />,

    'new-member':
      role === 'admin' ? <NewMember isSubmitting={setIsSubmitting} onUpdate={onUpdate} /> : defaultContent,

    members:
      role === 'admin' ? <MembersPanel isSubmitting={setIsSubmitting} /> : defaultContent,

    contacts: <ContactMembers isSubmitting={setIsSubmitting} adminMode={true} />,
    'my-contacts': <ContactMembers isSubmitting={setIsSubmitting} adminMode={false} />,

    // Panel de "Documentación Oficial"
    'official-docs':
      role === 'admin' ? (
        <OfficialDocsAdmin
          onOpenAnnualReports={() => goToTab('annual-reports')}
          onOpenStatutes={() => goToTab('statutes')}
          onOpenEthics={() => goToTab('ethics')}
          onOpenCrimePrevention={() => goToTab('crime-prevention')}
          onOpenProtocols={() => goToTab('protocols')}
          onOpenInternalRegulation={() => goToTab('internal-regulation')}
          onOpenMaterials={() => goToTab('material-content')}
          onOpenPopups={() => goToTab('popups')}
        />
      ) : (
        defaultContent
      ),

    chatbot:
      role === 'admin' ? <AdminChatbot /> : defaultContent,

    // Cards de administración (solo admin)
    'annual-reports':
      role === 'admin' ? <AnnualReportsCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'statutes':
      role === 'admin' ? <StatutesCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'ethics':
      role === 'admin' ? <EthicsReportsCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'crime-prevention':
      role === 'admin' ? <CrimePreventionCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'protocols': 
      role === 'admin' ? <ProtocolsCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'internal-regulation': 
      role === 'admin' ? <InternalRegulationCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'material-content':
      role === 'admin' ? <MaterialsReportsCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    'popups':
      role === 'admin' ? <PopupsCard onBack={() => goToTab('official-docs')} /> : defaultContent,

    fusion:
      role === 'admin' ? <FusionCompanies isSubmitting={setIsSubmitting} /> : defaultContent,

    'manage-quotas':
      role === 'admin'
        ? <MyQuotasAdmin isSubmitting={setIsSubmitting} />
        : role === 'socio'
          ? <MyQuotasPartner selectedSocio={detailsCompany} />
          : defaultContent,

    'change-rut': <ChangeRut isSubmitting={setIsSubmitting} onReload={onUpdate} />,
  };

  return views[tab] || defaultContent;
};

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [detailsCompany, setDetailsCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const { user, logout } = useAuth();
  const userRole = user.role;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const menuItems = getMenuItems(userRole);
  const location = useLocation();
  const [hasFetched, setHasFetched] = useState(false);

  // Lee ?tab= al entrar
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab) setActiveTab(tab);
  }, [location.search]);

  const goToTab = (tab) => {
    setActiveTab(tab);
    if (location.search) {
      window.history.replaceState(null, '', location.pathname);
    }
    window.scrollTo(0, 0);
  };

  const fetchCompanyDetails = async () => {
    if (hasFetched) return;
    setLoading(true);
    try {
      // 1) Intentar por RUT (variantes)
      const rut = user?.rut || localStorage.getItem('rut');
      let data = null;
      let lastErr = null;

      if (rut) {
        for (const v of rutVariants(rut)) {
          try {
            const res = await fetchWithAuth(`/companies/rut/${encodeURIComponent(v)}`, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
            });
            data = await jsonOrThrow(res);
            break; // éxito
          } catch (e) {
            lastErr = e;
            if (!isNotFoundLike(e)) throw e; // error real => salimos
          }
        }
      }

      // 2) Fallback por business_name SOLO si no encontramos por RUT
      if (!data && user?.business_name) {
        try {
          const res2 = await fetchWithAuth(`/companies/${encodeURIComponent(user.business_name)}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });
          data = await jsonOrThrow(res2);
        } catch (e2) {
          // si tampoco existe por nombre, propagamos el último error significativo
          throw lastErr || e2;
        }
      }

      if (!data) {
        throw lastErr || new Error('No se pudo localizar la empresa (RUT/nombre).');
      }

      setDetailsCompany(data);
      setHasFetched(true);
      setError(undefined);
    } catch (err) {
      setError(err);
      console.error('Error al obtener los detalles de la empresa:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !hasFetched) {
      fetchCompanyDetails();
    }
  }, [user, hasFetched]);

  const onUpdate = () => {
    setHasFetched(false);
    fetchCompanyDetails();
    setActiveTab('profile');
  };

  if (loading) return <PageLoader message="cargando su perfil..." />;
  if (error) return <ErrorPage />;

  const handlelogout = async () => {
    const confirmation = await Swal.fire({
      title: '¿Está seguro de que desea cerrar sesión?',
      text: 'Se cerrará la sesión de su cuenta.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
    });
    if (confirmation.isConfirmed) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <ActionLoader open={isSubmitting} onClose={() => {}} />

      {/* Mobile Header */}
      <header className="bg-white shadow-sm p-4 md:hidden flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {userRole === 'admin' ? 'Panel Administrativo' : 'Mi Cuenta'}
        </h1>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu size={24} />
        </button>
      </header>

      <div className="container mx-auto px-4 py-4 md:py-8 flex flex-col md:flex-row gap-8">
        {/* Sidebar */}
        <aside
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-64 bg-white rounded-xl shadow-md p-6 md:top-8 self-start`}
        >
          <LogoProfile detailsCompany={detailsCompany} userRole={userRole} isSubmitting={setIsSubmitting} />

          <nav className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {item.icon}
                <span className="flex-1 text-left">{item.label}</span>
                <span className="w-5 flex justify-end">
                  {activeTab === item.id && <ChevronRight size={18} />}
                </span>
              </button>
            ))}

            <div className="border-t border-gray-200 my-2" />

            <button
              onClick={() => {
                setActiveTab('settings');
                setIsMobileMenuOpen(false);
              }}
              className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'settings'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {icons.settings}
              Configuración
              {activeTab === 'settings' && <ChevronRight className="ml-auto" size={18} />}
            </button>

            <button
              onClick={handlelogout}
              className="w-full flex items-center px-4 py-3 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
            >
              <LogOut className="mr-3" size={18} />
              Cerrar Sesión
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="w-full overflow-x-hidden overflow-y-auto bg-white rounded-xl shadow-md p-4 md:p-6">
          {getContent(
            activeTab,
            userRole,
            detailsCompany,
            onUpdate,
            setIsSubmitting,
            goToTab
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
